import { Dialog, PlainButton } from "@jugl-web/ui-components";
import { Button, TextInput } from "@jugl-web/ui-kit";
import { isValidHttpUrl, useToast, useTranslations } from "@jugl-web/utils";
import React, { useState } from "react";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import { ReactComponent as ShopifyIcon } from "./assets/shopify.svg";
import { ReactComponent as ArrowRightIcon } from "./assets/arrow-right.svg";
import { ReactComponent as SettingsIcon } from "./assets/settings.svg";
import { NeedHelpAccordion } from "./components/NeedHelpAccordion";

export interface ShopifyInstallDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ShopifyInstallDialog: React.FC<ShopifyInstallDialogProps> = ({
  isOpen,
  onClose,
}) => {
  const { integrationsApi } = useRestApiProvider();
  const { entityId } = useEntitySelectedProvider();
  const { toast } = useToast();
  const { t } = useTranslations();
  const [installLink, setInstallLink] = useState("");
  const [isInstallLinkInvalid, setIsInstallLinkInvalid] = useState(false);
  const [clientId, setClientId] = useState("");
  const [secret, setSecret] = useState("");

  const [createShopifyConnection, { isLoading }] =
    integrationsApi.useCreateShopifyConnectionMutation();

  const handleClose = () => {
    setInstallLink("");
    setIsInstallLinkInvalid(false);
    setClientId("");
    setSecret("");
    onClose();
  };

  const validateInstallLink = (value: string) => {
    const trimmed = value.trim();
    return trimmed === "" || isValidHttpUrl(trimmed, true);
  };

  const handleInstallLinkBlur = () => {
    setIsInstallLinkInvalid(!validateInstallLink(installLink));
  };

  const handleInstall = async () => {
    const trimmedLink = installLink.trim();

    if (!validateInstallLink(trimmedLink)) {
      setIsInstallLinkInvalid(true);
      return;
    }

    try {
      await createShopifyConnection({
        entityId,
        data: {
          install_url: trimmedLink,
          client_id: clientId.trim(),
          client_secret: secret.trim(),
        },
      }).unwrap();
    } catch {
      toast(
        t({
          id: "feedback.something-went-wrong",
          defaultMessage: "Something went wrong",
        }),
        { variant: "error" }
      );
      return;
    }
    window.open(trimmedLink, "_blank", "noopener,noreferrer");
    handleClose();
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      className="flex max-h-[90vh] w-[780px] flex-col overflow-hidden rounded-2xl"
    >
      <div className="flex h-[60px] shrink-0 items-center justify-between border-0 border-b border-solid border-dark-100 px-6">
        <div className="flex items-center gap-3">
          <SettingsIcon />
          <span className="font-secondary font-semibold text-[#383838]">
            {t({
              id: "cx-actions-page.shopify-install-title",
              defaultMessage: "Settings",
            })}
          </span>
        </div>
        <PlainButton
          onClick={handleClose}
          className="flex items-center justify-center"
        >
          <CloseIcon />
        </PlainButton>
      </div>
      <div className="jugl__custom-scrollbar flex min-h-0 flex-1 flex-col overflow-y-auto px-14 py-10">
        <div className="flex items-center gap-3">
          <ShopifyIcon className="h-8 w-8" />
          <div className="flex items-center gap-4">
            <span className="font-secondary text-lg font-semibold text-[#1A2023]">
              {t({
                id: "cx-actions-page.shopify",
                defaultMessage: "Shopify",
              })}
            </span>
            <span className="font-secondary text-xs text-dark-600">
              {t({
                id: "cx-actions-page.shopify-install-subtitle",
                defaultMessage:
                  "Enter the install link to connect Account to Jugl",
              })}
            </span>
          </div>
        </div>
        <div className="mt-8 flex flex-col gap-6">
          <TextInput
            value={installLink}
            onChange={(event) => {
              setInstallLink(event.target.value.trimStart());
              if (isInstallLinkInvalid) {
                setIsInstallLinkInvalid(false);
              }
            }}
            onBlur={handleInstallLinkBlur}
            isInvalid={isInstallLinkInvalid}
            errorMessage={
              isInstallLinkInvalid
                ? t({
                    id: "cx-actions-page.shopify-install-incorrect-url",
                    defaultMessage: "Incorrect URL format",
                  })
                : undefined
            }
            label={t({
              id: "cx-actions-page.shopify-install-placeholder",
              defaultMessage: "Install Link",
            })}
            labelPosition="border-overlap"
            placeholder={t({ id: "common.enter", defaultMessage: "Enter" })}
          />
          <TextInput
            value={clientId}
            onChange={(event) => setClientId(event.target.value.trimStart())}
            label={t({
              id: "cx-actions-page.client-id",
              defaultMessage: "Client ID",
            })}
            labelPosition="border-overlap"
            placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
          />
          <TextInput
            value={secret}
            onChange={(event) => setSecret(event.target.value.trimStart())}
            type="password"
            autoComplete="new-password"
            label={t({
              id: "cx-actions-page.secret",
              defaultMessage: "Secret",
            })}
            labelPosition="border-overlap"
            placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
          />
        </div>
        <div className="mt-[72px]">
          <NeedHelpAccordion />
        </div>
      </div>
      <div className="flex shrink-0 justify-end border-0 border-t border-solid border-dark-100 px-6 py-4">
        <Button
          onClick={handleInstall}
          isDisabled={
            !installLink.trim() ||
            !clientId.trim() ||
            !secret.trim() ||
            isInstallLinkInvalid
          }
          isLoading={isLoading}
          iconEnd={<ArrowRightIcon />}
          className="w-[300px]"
        >
          {isLoading
            ? t({
                id: "cx-actions-page.processing",
                defaultMessage: "Processing...",
              })
            : t({
                id: "cx-actions-page.save-and-install",
                defaultMessage: "Save & Install",
              })}
        </Button>
      </div>
    </Dialog>
  );
};
